import * as React from 'react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InlineLabelProps {
  /** Label content rendered in the truncating center span. */
  children: React.ReactNode;
  /** Additional classes applied to the outer flex wrapper. */
  className?: string;
  /** Additional classes applied to the label span (use for color, weight, italic overrides). */
  labelClassName?: string;
  /** Optional leading adornment (avatar, icon, badge, etc.). */
  prefix?: React.ReactNode;
  /** Size variant — controls the flex gap and label font-size. */
  size?: 'sm' | 'xs';
  /** Optional trailing adornment. */
  suffix?: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const WRAPPER_GAP: Record<NonNullable<InlineLabelProps['size']>, string> = {
  sm: 'gap-2',
  xs: 'gap-1.5',
};

const LABEL_SIZE: Record<NonNullable<InlineLabelProps['size']>, string> = {
  sm: 'text-sm',
  xs: 'text-xs',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A truncating label with optional leading and/or trailing adornment slots.
 *
 * The `prefix` slot accepts any node (avatar, icon, badge, or nothing), and
 * `suffix` provides a symmetric trailing slot.
 *
 * Two size variants:
 *   - `xs` (default) — gap-1.5, text-xs
 *   - `sm` — gap-2, text-sm
 *
 * Intentional per-site overrides (muted colour, italic, font-weight) are
 * expressed via `labelClassName`. Use `className` to adjust the wrapper itself.
 */
export function InlineLabel({
  children,
  className,
  labelClassName,
  prefix,
  size = 'xs',
  suffix,
}: InlineLabelProps): React.JSX.Element {
  return (
    <div className={cn('flex items-center min-w-0', WRAPPER_GAP[size], className)}>
      {prefix}
      <span className={cn(LABEL_SIZE[size], 'truncate', labelClassName)}>{children}</span>
      {suffix}
    </div>
  );
}
